// src/pages/ManageTeachers.js
/* eslint-disable */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import "../App.css";
import Papa from "papaparse";
import toast, { Toaster } from "react-hot-toast";
import { auth } from "../firebase";

import {
  collection,
  onSnapshot,
  query,
  where,
  doc,
  setDoc,
  deleteDoc,
  serverTimestamp,
  getDoc,
  getDocs,
  limit,
} from "firebase/firestore";
import { db } from "../firebase";

// Optional secondary app bootstrap (kept but unused by default)
import { initializeApp, getApp, getApps } from "firebase/app";

import { getFunctions, httpsCallable } from "firebase/functions";

const ManageTeachers = () => {
  const navigate = useNavigate();
  const schoolId = localStorage.getItem("schoolId") || "";

  const [teachers, setTeachers] = useState([]);
  const [classes, setClasses] = useState([]);
  const [newTeacher, setNewTeacher] = useState({
    firstName: "",
    lastName: "",
    email: "",
    className: "",
  });
  const [csvRows, setCsvRows] = useState([]);
  // ✅ temp passwords ONLY for this screen session (like students)
const [tempPwMap, setTempPwMap] = useState({}); // { [uid]: "password" }


  // Optional secondary app (not used unless you re-enable client-side auth creation)
  try {
    const baseApp = getApp();
    const name = "ysw-secondary";
    getApps().find((a) => a.name === name) || initializeApp(baseApp.options, name);
  } catch {
    /* benign */
  }

  const functions = getFunctions(undefined, "us-central1");
  const adminCreateTeacher = httpsCallable(functions, "adminCreateTeacher");
  const adminDeleteTeacherUser = httpsCallable(functions, "adminDeleteTeacherUser");
  const adminResetTeacherPassword = httpsCallable(functions, "adminResetTeacherPassword");

  const [resetModalOpen, setResetModalOpen] = useState(false);
  const [resetForTeacher, setResetForTeacher] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [resetSaving, setResetSaving] = useState(false);



  /* ------------------------------- Live data ------------------------------- */
  const classroomsWarned = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        if (auth.currentUser) await auth.currentUser.getIdToken(true);
      } catch {}
    })();
  }, []);

  useEffect(() => {
    if (!schoolId) {
      toast.error("No school selected. Open School Dashboard first.");
      return;
    }

    // Teachers list
    const tq = query(collection(db, "teachers"), where("schoolId", "==", schoolId));
    const unsubTeachers = onSnapshot(
      tq,
      (snap) =>
        setTeachers(
          snap.docs.map((d) => ({ id: d.id, ...d.data(), uid: d.data().uid || d.id }))
        ),
      (err) => {
        console.error("teachers onSnapshot error:", err);
      }
    );

    // Classrooms list (non-critical; do not spam toasts)
    const cq = collection(db, "schools", schoolId, "classrooms");
    const unsubClasses = onSnapshot(
      cq,
      (snap) => setClasses(snap.docs.map((d) => d.id)),
      (err) => {
        if (!classroomsWarned.current) {
          console.warn("classrooms onSnapshot error:", err?.code || err?.message || err);
          classroomsWarned.current = true;
        }
      }
    );

    return () => {
      unsubTeachers();
      unsubClasses();
    };
  }, [schoolId]);

  /* ------------------------------- Utilities -------------------------------- */
  const tempPassword = () => uuidv4().slice(0, 8);

  const msgFromError = (err) => {
    if (!err) return "Unknown error";
    if (typeof err?.message === "string") return err.message;
    if (err?.code) return String(err.code);
    return String(err);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewTeacher((t) => ({ ...t, [name]: value }));
  };

    async function writeTeacherDocs(uid, { firstName, lastName, email, password, className }) {
    // 1) teachers/{uid}
    await setDoc(
      doc(db, "teachers", uid),
      {
        uid,
        firstName,
        lastName,
        email,
        emailLower: (email || "").toLowerCase(),
        schoolId,
        className,
        role: "teacher",
        temporaryPassword: password,
        createdAt: serverTimestamp(),
      },
      { merge: true }
    );

    // 2) Mirrors (best-effort)
    if (className) {
      try {
        await setDoc(
          doc(db, "schools", schoolId, "classrooms", className),
          { id: className, name: className, updatedAt: serverTimestamp() },
          { merge: true }
        );
      } catch (e) {
        console.warn("[mirror] classroom upsert blocked:", msgFromError(e));
      }
      try {
        await setDoc(
          doc(db, "schools", schoolId, "classrooms", className, "teachers", uid),
          { uid, firstName, lastName, email, createdAt: serverTimestamp() },
          { merge: true }
        );
      } catch (e) {
        console.warn("[mirror] classroom/teachers mirror blocked:", msgFromError(e));
      }
    }
  }

  /**
   * Create teacher:
   * - Uses adminCreateTeacher callable (creates Auth user)
   * - Then writes Firestore docs from client (this matches your older “create works” flow)
   */
  async function createTeacherUserAndDoc({ firstName, lastName, email, className }) {
  const password = tempPassword();

  // Server creates Auth user + Firestore teacher/users mirrors
  const resp = await adminCreateTeacher({
    schoolId,
    firstName,
    lastName,
    email,
    className,
    password,
  });

  const uid = resp?.data?.uid;
  if (!uid) throw new Error(resp?.data?.error || "Admin create teacher failed.");

  // ✅ IMPORTANT: no Firestore writes from the client
  return { uid, password };
}



  /* -------------------------------- Handlers -------------------------------- */
  const handleAddTeacher = async () => {
    const { firstName, lastName, email, className } = newTeacher;

    if (!schoolId) {
      toast.error("No school selected. Go back to School Dashboard first.");
      return;
    }
    if (!firstName || !lastName || !email || !className) {
      toast.error("All fields are required.");
      return;
    }

    // Non-blocking school doc check (keep silent; many accounts can't read /schools)
    getDoc(doc(db, "schools", schoolId)).catch(() => {});

    try {
      const { uid, password } = await createTeacherUserAndDoc({
        firstName,
        lastName,
        email,
        className,
      });

     // ✅ store password only in-memory for this session
setTempPwMap((prev) => ({ ...prev, [uid]: password }));

// Optimistic UI (but do NOT store password in teacher object)
setTeachers((prev) => {
  const filtered = prev.filter((t) => (t.uid || t.id) !== uid);
  return [
    ...filtered,
    {
      uid,
      firstName,
      lastName,
      email,
      className,
      schoolId,
      role: "teacher",
    },
  ];
});


      setNewTeacher({ firstName: "", lastName: "", email: "", className: "" });
      toast.success("✅ Teacher account created.");
    } catch (e) {
      console.error("❌ Error creating teacher:", e);
      const m = msgFromError(e);

      let confirmedSaved = false;
      try {
        const lowerEmail = (newTeacher.email || "").toLowerCase();
        const q = query(
          collection(db, "teachers"),
          where("schoolId", "==", schoolId),
          where("emailLower", "==", lowerEmail),
          limit(1)
        );
        const snap = await getDocs(q);
        if (!snap.empty) confirmedSaved = true;
      } catch {}

      if (confirmedSaved) {
        toast.success("✅ Teacher has been saved successfully.");
        setNewTeacher({ firstName: "", lastName: "", email: "", className: "" });
        return;
      }

      if (/already.*exist/i.test(m) || /email[-\s]?already[-\s]?in[-\s]?use/i.test(m)) {
        toast.error("Email already in use. This teacher may already have an account.");
      } else if (/permission|denied|insufficient/i.test(m)) {
        toast.error("You don’t have permission to add teachers for this school.");
      } else {
        toast.error(`❌ Failed to create teacher: ${m}`);
      }
    }
  };

  /**
   * ✅ FIXED DELETE:
   * - Removes from TABLE immediately (so user sees it disappear)
   * - Then attempts server + firestore cleanup
   * - If it fails, puts the row back
   */
  const handleDeleteTeacher = async (t) => {
    const teacher =
      typeof t === "string" ? teachers.find((x) => x.uid === t || x.id === t) : t;
    if (!teacher?.uid) return;

    const yes = window.confirm(
      `Delete teacher ${teacher.firstName} ${teacher.lastName}?`
    );
    if (!yes) return;

    // 1) Optimistic UI removal (THIS is what you said was missing)
    const prevTeachers = teachers;
    setTeachers((prev) => prev.filter((x) => (x.uid || x.id) !== teacher.uid));

    try {
      // 2) Authoritative server-side delete (Auth + Firestore) — requires correct payload
      await adminDeleteTeacherUser({
        uid: teacher.uid,
        schoolId,
        className: teacher.className || null,
      });

      // 3) Best-effort direct deletes (in case your function only deletes Auth)
      //    These may fail due to rules — ignore errors.
      try { await deleteDoc(doc(db, "teachers", teacher.uid)); } catch {}
      if (teacher.className) {
        try {
          await deleteDoc(
            doc(db, "schools", schoolId, "classrooms", teacher.className, "teachers", teacher.uid)
          );
        } catch {}
      }
      try { await deleteDoc(doc(db, "users", teacher.uid)); } catch {}

      toast.success("✅ Teacher deleted.");
    } catch (e) {
      console.error("❌ Error deleting teacher:", e);

      // Roll back UI so it doesn't "fake delete"
      setTeachers(prevTeachers);

      toast.error(`❌ Failed to delete teacher: ${msgFromError(e)}`);
    }
  };

  const handleCsvUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        const rows = (results.data || [])
          .map((r) => ({
            firstName: r.firstName || r.FirstName || "",
            lastName: r.lastName || r.LastName || "",
            email: r.email || r.Email || "",
            className: r.className || r.ClassName || "",
          }))
          .filter((r) => r.firstName && r.lastName && r.email && r.className);

        setCsvRows(rows);
        toast.success(`Found ${rows.length} valid rows.`);
      },
    });
  };

  const handleBulkAddTeachers = async () => {
    if (!csvRows.length) return;
    if (!schoolId) {
      toast.error("No school selected. Go back to School Dashboard first.");
      return;
    }

    let ok = 0, fail = 0;
    for (const row of csvRows) {
  try {
    const { uid, password } = await createTeacherUserAndDoc(row);
    setTempPwMap((prev) => ({ ...prev, [uid]: password }));
    ok++;
  } catch (e) {

        console.error("Bulk teacher create failed:", row.email, e);
        fail++;
      }
    }
    setCsvRows([]);
    toast.success(`Bulk complete: ${ok} added${fail ? `, ${fail} failed` : ""}.`);
  };

  const generatePassword = () => uuidv4().replace(/-/g, "").slice(0, 10); // 10 chars

  const openResetPassword = (t) => {
  setResetForTeacher(t);
  setNewPw(generatePassword());
  setResetModalOpen(true);
};

const handleConfirmResetPassword = async () => {
  if (!resetForTeacher?.uid) return;
  if (!newPw || newPw.length < 6) return toast.error("Password must be at least 6 characters.");

  setResetSaving(true);
  try {
    await adminResetTeacherPassword({
      uid: resetForTeacher.uid,
      schoolId,
      newPassword: newPw,
    });

    toast.success("✅ Password reset. Copy and share it now.");
    // Do NOT save to Firestore.
    // Keep modal open so they can copy.
    // ✅ show it in the table ONLY for this session
setTempPwMap((prev) => ({ ...prev, [resetForTeacher.uid]: newPw }));

  } catch (e) {
    console.error(e);
    toast.error(e?.message || "Failed to reset password.");
  } finally {
    setResetSaving(false);
  }
};


  /* --------------------------------- Render -------------------------------- */
  return (
    <div
      className="page-container"
      style={{
        backgroundImage: `url(${require("../assets/images/landing_background.jpg")})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        minHeight: "100vh",
        fontFamily: "Montserrat, sans-serif",
      }}
    >
      <header className="page-header">
        <div className="logo">
          <img
            src={require("../assets/images/logo_name.PNG")}
            alt="YourStoryWorld Logo"
            style={{ height: "50px", cursor: "pointer" }}
            onClick={() => navigate("/")}
          />
        </div>
        <nav style={{ display: "flex", gap: "15px", justifyContent: "flex-end" }}>
          <button onClick={() => navigate("/help")} className="header-button">
            Help
          </button>
          <button onClick={() => navigate("/")} className="header-button">
            Home
          </button>
          <button onClick={() => navigate("/school-dashboard")} className="header-button">
            Back to Dashboard
          </button>
        </nav>
      </header>

      <Toaster position="top-center" reverseOrder={false} />

      <main className="page-content" style={{ textAlign: "center", padding: "20px" }}>
        <div
          className="dashboard-card"
          style={{
            background: "rgba(255, 255, 255, 0.95)",
            padding: "30px",
            borderRadius: "10px",
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
            maxWidth: "1000px",
            margin: "0 auto 40px",
          }}
        >
          <h1 style={{ color: "#007BFF" }}>Manage Teachers</h1>

          <h2 style={{ color: "#333", marginTop: "30px" }}>Add a New Teacher</h2>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "10px",
              justifyContent: "center",
              marginBottom: "20px",
            }}
          >
            <input
              type="text"
              name="firstName"
              placeholder="First Name"
              value={newTeacher.firstName}
              onChange={handleInputChange}
              className="input"
              style={{ padding: "10px", borderRadius: "5px", border: "1px solid #ccc" }}
            />
            <input
              type="text"
              name="lastName"
              placeholder="Last Name"
              value={newTeacher.lastName}
              onChange={handleInputChange}
              className="input"
              style={{ padding: "10px", borderRadius: "5px", border: "1px solid #ccc" }}
            />
            <input
              type="email"
              name="email"
              placeholder="Email"
              value={newTeacher.email}
              onChange={handleInputChange}
              className="input"
              style={{ padding: "10px", borderRadius: "5px", border: "1px solid #ccc" }}
            />
            <input
              type="text"
              name="className"
              placeholder="Enter or select class"
              list="classOptions"
              value={newTeacher.className}
              onChange={handleInputChange}
              className="input"
              style={{ padding: "10px", borderRadius: "5px", border: "1px solid #ccc" }}
            />
            <datalist id="classOptions">
              {classes.map((cls) => (
                <option key={cls} value={cls} />
              ))}
            </datalist>

            <button onClick={handleAddTeacher} className="student-button">
              Add Teacher
            </button>
          </div>

          <h3>Bulk Upload from CSV</h3> <p style={{ marginTop: 6, color: "#555" }}> Expected headers: <code>FirstName, LastName, Email, ClassName</code> </p> <input type="file" accept=".csv" onChange={handleCsvUpload} style={{ margin: "10px 0" }} /> {csvRows.length > 0 && ( <button onClick={handleBulkAddTeachers} className="student-button"> Proceed Upload ({csvRows.length}) </button> )}


          <h2 style={{ color: "#333", marginTop: "30px" }}>Teacher List</h2>
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontFamily: "Montserrat, sans-serif",
              }}
            >
              <thead>
                <tr style={{ backgroundColor: "#e0f0ff" }}>
                  <th style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>
                    First Name
                  </th>
                  <th style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>
                    Last Name
                  </th>
                  <th style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>
                    Email
                  </th>
                  <th style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>
                    Temp Password
                  </th>
                  <th style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>
                    Class
                  </th>
                  <th style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {teachers.map((t) => (
                  <tr key={t.uid || t.id}>
                    <td style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>
                      {t.firstName}
                    </td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>
                      {t.lastName}
                    </td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>
                      {t.email}
                    </td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>
                      {tempPwMap[t.uid || t.id] || "—"}
                    </td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>
                      {t.className}
                    </td>
                    <td style={{ padding: "10px", borderBottom: "1px solid #ddd" }}>
  <button
    onClick={() => openResetPassword(t)}
    className="student-button"
    style={{ marginRight: 10 }}
  >
    Reset Password
  </button>

  <button onClick={() => handleDeleteTeacher(t)} className="delete-button">
    Delete
  </button>
</td>

                  </tr>
                ))}
                {teachers.length === 0 && (
                  <tr>
                    <td colSpan={6} style={{ padding: 20, color: "#666" }}>
                      No teachers found for this school.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {resetModalOpen && (
  <div
    onClick={() => setResetModalOpen(false)}
    style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.5)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 9999,
      padding: 20,
    }}
  >
    <div
      onClick={(e) => e.stopPropagation()}
      style={{
        width: "min(520px, 95vw)",
        background: "#fff",
        borderRadius: 12,
        padding: 20,
        textAlign: "left",
        boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
        fontFamily: "Montserrat, sans-serif",
      }}
    >
      <h2 style={{ marginTop: 0, color: "#007BFF" }}>Reset Teacher Password</h2>

      <p style={{ marginTop: 0, color: "#555" }}>
        Teacher: <strong>{resetForTeacher?.firstName} {resetForTeacher?.lastName}</strong>
        <br />
        Email: <strong>{resetForTeacher?.email}</strong>
      </p>

      <label style={{ fontWeight: 700, display: "block", marginBottom: 6 }}>New Password</label>
      <input
        value={newPw}
        onChange={(e) => setNewPw(e.target.value)}
        style={{
          width: "100%",
          padding: 10,
          borderRadius: 8,
          border: "1px solid #ccc",
          marginBottom: 10,
        }}
      />

      <div style={{ display: "flex", gap: 10, marginBottom: 10 }}>
        <button
          onClick={() => setNewPw(generatePassword())}
          className="student-button"
          type="button"
        >
          Regenerate
        </button>

        <button
          onClick={() => {
            navigator.clipboard?.writeText(newPw);
            toast.success("Copied to clipboard");
          }}
          className="student-button"
          type="button"
        >
          Copy
        </button>
      </div>

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
        <button
          onClick={() => setResetModalOpen(false)}
          className="header-button"
          disabled={resetSaving}
        >
          Close
        </button>

        <button
          onClick={handleConfirmResetPassword}
          className="student-button"
          disabled={resetSaving}
          style={{ fontWeight: 700 }}
        >
          {resetSaving ? "Saving..." : "Confirm Reset"}
        </button>
      </div>

      <p style={{ fontSize: 12, color: "#64748b", marginTop: 12 }}>
        This password will only be shown on this page for this session (it disappears after refresh).
      </p>
    </div>
  </div>
)}

      </main>

      <footer className="page-footer" style={{ textAlign: "center", padding: "20px 0" }}>
        <p>&copy; {new Date().getFullYear()} YourStoryWorld. All rights reserved.</p>
        <nav style={{ marginTop: "10px" }}>
          <a href="/about" className="footer-link">
            About Us
          </a>
          <a href="/contact" className="footer-link">
            Contact
          </a>
        </nav>
      </footer>
    </div>
  );
};

export default ManageTeachers;


